import { yupResolver } from '@hookform/resolvers/yup';
import Avatar from '-/Custom/Avatar';
import * as Notice from '-/Custom/Notice';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import { useClient, useMutation, useQuery } from 'urql';
import { v4 } from 'uuid';
import BadgeAssigneeEditAttribute from '@/components/Badge/BadgeAssigneeEditAttribute';
import BadgeMask from '@/components/Badge/BadgeMask';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import * as Header from '@/components/Custom/Header';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import Box from '@/components/UI/Box';
import Card from '@/components/UI/Card';
import FormContentBoundary from '@/components/UI/FormContentBoundary';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import pluralize from '@/utils/pluralize';
import {
  type BadgeAssigneeEditFormValues,
  badgeAssigneeEditValidator,
  getBadgeAssigneeEditFormValues,
  getTargetPersonIds,
  NO_BADGE_ASSIGNEE_ID,
} from './BadgeAssigneeEdit.form';

const UpsertBadgeAssignee = graphql(
  /* GraphQL */ `
    mutation UpsertBadgeAssignee(
      $id: uuid
      $badgeId: uuid!
      $personId: String
      $eventId: uuid
      $teamId: uuid
      $assigneeAttributes: [badge_assignee_attribute_insert_input!]!
    ) {
      insert_badge_assignee_one(
        on_conflict: {
          constraint: badge_assignee_pkey
          update_columns: [start_datetime, end_datetime]
        }
        object: {
          id: $id
          badge_id: $badgeId
          person_id: $personId
          event_id: $eventId
          team_id: $teamId
          badge_assignee_attributes: {
            on_conflict: {
              constraint: badge_assignee_attribute_pkey
              update_columns: [text_value, numeric_value]
            }
            data: $assigneeAttributes
          }
        }
      ) {
        badge_id
        person_id
        event_id
        team_id
        person {
          id
          badge_assignments {
            id
            badge_id
            person_id
            event_id
            event {
              id
              title
            }
          }
        }
      }
    }
  `,
);

const InsertBadgeAssignees = graphql(/* GraphQL */ `
  mutation InsertBadgeAssignees(
    $assignments: [badge_assignee_insert_input!]!
    $assigneeAttributes: [badge_assignee_attribute_insert_input!]!
  ) {
    insert_badge_assignee(objects: $assignments) {
      affected_rows
    }
    insert_badge_assignee_attribute(
      objects: $assigneeAttributes
      on_conflict: {
        constraint: badge_assignee_attribute_badge_assignee_id_badge_attribute_id_k
        update_columns: []
      }
    ) {
      affected_rows
    }
  }
`);

export const GetBadgeAssigneeCreateForm = graphql(
  /* GraphQL */ `
    query GetBadgeAssigneeCreateForm($badgeId: uuid!, $emptyAssigneeId: uuid!) {
      badge_by_pk(id: $badgeId) {
        id
        badge_attributes(order_by: { order: asc }) {
          id
          attribute_type
          default_value
          title
          options
          is_required
          order
          badge_attribute_assignees(
            where: { badge_assignee_id: { _eq: $emptyAssigneeId } }
            limit: 1
          ) {
            id
            value
          }
        }
      }
    }
  `,
);

const GetBadgeAssigneeExistingForm = graphql(
  /* GraphQL */ `
    query GetBadgeAssigneeExistingForm($assigneeId: uuid!) {
      badge_assignee_by_pk(id: $assigneeId) {
        id
        badge {
          id
          badge_attributes(order_by: { order: asc }) {
            id
            attribute_type
            default_value
            title
            options
            is_required
            order
            badge_attribute_assignees(
              where: { badge_assignee_id: { _eq: $assigneeId } }
              limit: 1
            ) {
              id
              value
            }
          }
        }
      }
    }
  `,
);

const GetBadgeAssigneeCreateInfo = graphql(
  /* GraphQL */ `
    query GetBadgeAssigneeCreateInfo(
      $badgeId: uuid!
      $eventId: uuid!
      $personId: String!
      $teamId: uuid!
      $targetPersonIds: [String!]!
      $includeEvent: Boolean!
      $includeSinglePerson: Boolean!
      $includeTeam: Boolean!
      $includeTargetPeople: Boolean!
      $targetPeoplePreviewLimit: Int!
    ) {
      event_by_pk(id: $eventId) @include(if: $includeEvent) {
        id
        title
        event_type
      }
      person_by_pk(id: $personId) @include(if: $includeSinglePerson) {
        id
        name
        color
        avatar_id
      }
      person(
        where: { id: { _in: $targetPersonIds } }
        order_by: { name: asc }
        limit: $targetPeoplePreviewLimit
      ) @include(if: $includeTargetPeople) {
        id
        name
        color
        avatar_id
      }
      person_aggregate(where: { id: { _in: $targetPersonIds } })
        @include(if: $includeTargetPeople)
      {
        aggregate {
          count
        }
      }
      team_by_pk(id: $teamId) @include(if: $includeTeam) {
        id
        title
      }
      badge_by_pk(id: $badgeId) {
        id
        color
        title
      }
    }
  `,
);

const GetBadgeAssigneeEditInfo = graphql(
  /* GraphQL */ `
    query GetBadgeAssigneeEditInfo($assigneeId: uuid!) {
      badge_assignee_by_pk(id: $assigneeId) {
        id
        badge_id
        person_id
        event_id
        team_id
        created_at
        updated_at
        event {
          id
          title
          event_type
        }
        team {
          id
          title
        }
        creator {
          id
          name
          color
          avatar_id
        }
        updater {
          id
          name
          color
          avatar_id
        }
        person {
          id
          name
          color
          avatar_id
        }
        badge {
          id
          color
          title
        }
      }
    }
  `,
);

const TARGET_PEOPLE_PREVIEW_LIMIT = 5;

export default function BadgeAssigneeEdit() {
  const client = useClient();
  const { badgeId, assigneeId, teamId, personId, eventId, selected } =
    useLocalSearchParams<{
      badgeId?: string;
      assigneeId?: string;
      teamId?: string;
      personId?: string;
      eventId?: string;
      selected?: string;
    }>();

  const form = useForm<BadgeAssigneeEditFormValues>({
    mode: 'onChange',
    resolver: yupResolver(badgeAssigneeEditValidator),
    defaultValues: async () => {
      if (assigneeId) {
        const { data } = await client.query(
          GetBadgeAssigneeExistingForm,
          {
            assigneeId,
          },
          { requestPolicy: 'network-only' },
        );
        const badge = data?.badge_assignee_by_pk?.badge;

        return getBadgeAssigneeEditFormValues({
          assigneeId,
          badgeAttributes: badge?.badge_attributes ?? [],
          badgeId: badge?.id,
        });
      }

      const { data } = await client.query(
        GetBadgeAssigneeCreateForm,
        {
          badgeId: badgeId ?? '',
          emptyAssigneeId: NO_BADGE_ASSIGNEE_ID,
        },
        { requestPolicy: 'network-only' },
      );
      const badge = data?.badge_by_pk;

      return getBadgeAssigneeEditFormValues({
        badgeAttributes: badge?.badge_attributes ?? [],
        badgeId: badge?.id ?? badgeId,
      });
    },
  });

  return (
    <FormProvider {...form}>
      <BadgeAssigneeEditContent
        assigneeId={assigneeId}
        badgeId={badgeId}
        eventId={eventId}
        personId={personId}
        selected={selected}
        teamId={teamId}
      />
    </FormProvider>
  );
}

type BadgeAssigneeEditContentProps = {
  assigneeId?: string;
  badgeId?: string;
  eventId?: string;
  loadingForm?: boolean;
  onSaved?: () => void;
  personId?: string;
  selected?: string;
  showHeaderLeft?: boolean;
  teamId?: string;
};

export function BadgeAssigneeEditContent({
  assigneeId,
  badgeId,
  eventId,
  loadingForm = false,
  onSaved,
  personId,
  selected,
  showHeaderLeft = true,
  teamId,
}: BadgeAssigneeEditContentProps) {
  const { back, push } = useRouter();
  const [, upsertBadgeAssignee] = useMutation(UpsertBadgeAssignee);
  const [, insertBadgeAssignees] = useMutation(InsertBadgeAssignees);
  const targetPersonIds = getTargetPersonIds({ personId, selected });
  const firstTargetPersonId = targetPersonIds[0] ?? '';
  const isBulkCreate = !assigneeId && targetPersonIds.length > 1;

  const [{ data: assigneeData }] = useQuery({
    query: GetBadgeAssigneeEditInfo,
    variables: {
      assigneeId: assigneeId ?? NO_BADGE_ASSIGNEE_ID,
    },
    requestPolicy: 'network-only',
    pause: !assigneeId,
  });

  const [{ data: infoData }] = useQuery({
    query: GetBadgeAssigneeCreateInfo,
    variables: {
      badgeId: badgeId ?? '',
      eventId: eventId ?? NO_BADGE_ASSIGNEE_ID,
      personId: firstTargetPersonId,
      teamId: teamId ?? NO_BADGE_ASSIGNEE_ID,
      targetPersonIds,
      includeEvent: Boolean(eventId),
      includeSinglePerson: Boolean(firstTargetPersonId && !isBulkCreate),
      includeTeam: Boolean(teamId),
      includeTargetPeople: isBulkCreate,
      targetPeoplePreviewLimit: TARGET_PEOPLE_PREVIEW_LIMIT,
    },
    pause: Boolean(assigneeId) || !badgeId || !targetPersonIds.length,
  });

  const assignee = assigneeData?.badge_assignee_by_pk;
  const badge = assignee?.badge ?? infoData?.badge_by_pk;
  const assignmentEvent = assignee?.event ?? infoData?.event_by_pk;
  const assignmentTeam = assignee?.team ?? infoData?.team_by_pk;
  const targetPeople = infoData?.person ?? [];
  const targetPeopleCount =
    infoData?.person_aggregate?.aggregate?.count ?? targetPeople.length;
  const person = assignee?.person ?? infoData?.person_by_pk ?? targetPeople[0];
  const targetLabel = isBulkCreate
    ? `${targetPeopleCount} ${pluralize('person', targetPeopleCount)}`
    : person?.name;
  const resolvedBadgeId = assignee?.badge_id ?? badgeId;
  const resolvedEventId = assignee?.event_id ?? eventId;
  const resolvedPersonId = assignee?.person_id ?? firstTargetPersonId;
  const resolvedTeamId = assignee?.team_id ?? teamId;

  const form = useFormContext<BadgeAssigneeEditFormValues>();
  const {
    control,
    handleSubmit,
    formState: { isLoading, isValid, isSubmitting },
  } = form;
  const fetchingForm = isLoading || loadingForm;
  const onComplete = onSaved ?? back;

  const attributes = useFieldArray({
    name: 'badge_attributes',
    control,
  });

  const hasAssignmentTarget = isBulkCreate
    ? Boolean(targetPersonIds.length)
    : Boolean(resolvedPersonId);
  const isFormAssignEnabled =
    isValid && !fetchingForm && !isSubmitting && hasAssignmentTarget;

  const onPressDeleteAssignment = () => {
    if (!assigneeId) {
      return;
    }

    push({
      pathname: '/badge-assignee/[assigneeId]/edit/delete',
      params: {
        assigneeId,
        assigneeIds: assigneeId,
        assignmentCount: '1',
      },
    });
  };

  const onSave = async (values: BadgeAssigneeEditFormValues) => {
    if (!assigneeId && isBulkCreate) {
      if (!resolvedBadgeId) {
        return;
      }

      const assignments = targetPersonIds.map((targetPersonId) => ({
        id: v4(),
        badge_id: resolvedBadgeId,
        person_id: targetPersonId,
        event_id: eventId,
        team_id: teamId,
      }));
      const assigneeAttributes = assignments.flatMap((assignment) =>
        values.badge_attributes.map((badgeAttribute) => {
          const assignmentValue = badgeAttribute.badge_attribute_assignees?.[0];
          const valueColumn =
            badgeAttribute?.attribute_type === 'text'
              ? 'text_value'
              : 'numeric_value';

          return {
            id: v4(),
            badge_assignee_id: assignment.id,
            badge_attribute_id: badgeAttribute.id,
            [valueColumn]: assignmentValue?.value || null,
          };
        }),
      );
      const { error } = await insertBadgeAssignees({
        assignments,
        assigneeAttributes,
      });

      if (!error) {
        onComplete();
      }

      return;
    }

    if (!resolvedBadgeId || !resolvedPersonId) {
      return;
    }

    const { error } = await upsertBadgeAssignee({
      id: assigneeId || v4(),
      badgeId: resolvedBadgeId,
      personId: resolvedPersonId,
      eventId: resolvedEventId,
      teamId: resolvedTeamId,
      assigneeAttributes: values.badge_attributes.map((badgeAttribute) => {
        const assignmentValue = badgeAttribute.badge_attribute_assignees?.[0];

        const valueColumn =
          badgeAttribute?.attribute_type === 'text'
            ? 'text_value'
            : 'numeric_value';

        return {
          id: assignmentValue?.id,
          badge_attribute_id: badgeAttribute.id,
          [valueColumn]: assignmentValue?.value || null,
        };
      }),
    });

    if (!error) {
      onComplete();
      return;
    }
  };

  const headerTitleText = badge?.title || 'Badge Assignment';
  const headerSubtitle = assigneeId ? 'Edit Assignment' : 'Assign Badge';
  const headerTitle = () => (
    <Header.TitleContainer>
      <BadgeMask size="md" color={badge?.color}>
        <Avatar
          ring={false}
          size="md"
          color={badge?.color || ''}
          name={headerTitleText}
        />
      </BadgeMask>
      <Box flex>
        <Text
          flexShrink={1}
          numberOfLines={1}
          size="xl"
          testID="BadgeAssigneeEditHeaderTitle"
        >
          {headerTitleText}
        </Text>
        <Text variant="typographySecondary" size="xs" numberOfLines={1}>
          {headerSubtitle}
        </Text>
      </Box>
    </Header.TitleContainer>
  );
  const headerRight = () =>
    assigneeId ? (
      <Header.End>
        <Header.ButtonGroup>
          <Header.Button
            accessibilityLabel="Delete assignment"
            testID="BadgeAssigneeEditDeleteButton"
            onPress={onPressDeleteAssignment}
          >
            <Icon name="trash-outline" size="2xl" />
          </Header.Button>
        </Header.ButtonGroup>
      </Header.End>
    ) : null;
  const screenOptions = showHeaderLeft
    ? {
        headerRight,
        headerTitle,
        title: headerSubtitle,
      }
    : {
        headerRight,
        headerTitle,
        title: headerSubtitle,
      };

  return (
    <>
      <Stack.Screen options={screenOptions} />
      <FormContentBoundary loading={fetchingForm}>
        {!assigneeId && !attributes.fields.length ? (
          <Notice.Container flex={1}>
            <Box direction="horizontal" p="xl" align="center" space="sm">
              <BadgeMask size="2xl" color={badge?.color}>
                <Avatar
                  size="2xl"
                  color={badge?.color || ''}
                  name={badge?.title || ''}
                />
              </BadgeMask>
              <Icon size="4xl" name="arrow-forward-outline" />
              <Avatar
                id={person?.avatar_id || ''}
                size="2xl"
                color={person?.color || ''}
                name={targetLabel || ''}
              />
            </Box>
            <Notice.Text>
              Assign{' '}
              <Notice.TextEntity color={badge?.color}>
                {badge?.title}
              </Notice.TextEntity>{' '}
              to{' '}
              <Notice.TextEntity color={person?.color || badge?.color || ''}>
                {targetLabel}
              </Notice.TextEntity>
              ?
            </Notice.Text>
          </Notice.Container>
        ) : null}

        {assigneeId || attributes.fields.length ? (
          <ModalKeyboardAwareScrollView
            contentContainerStyle={style.inspectorContent}
          >
            <AssignmentOverviewCard
              assignee={assignee}
              badgeColor={badge?.color}
              eventId={resolvedEventId}
              eventTitle={assignmentEvent?.title}
              eventType={assignmentEvent?.event_type}
              isBulkCreate={isBulkCreate}
              targetLabel={targetLabel}
              person={person}
              teamId={resolvedTeamId}
              teamTitle={assignmentTeam?.title}
            />

            {attributes.fields.length ? (
              <AssignmentAttributeCards attributes={attributes.fields} />
            ) : null}
          </ModalKeyboardAwareScrollView>
        ) : null}

        <ModalKeyboardStickyView>
          <Box direction="horizontal">
            {assigneeId && attributes.fields.length ? (
              <FormSaveButton
                info={{ text: 'Save' }}
                onPress={() => handleSubmit(onSave)()}
                containerStyle={style.submitButton}
              />
            ) : null}
            {!assigneeId ? (
              <FormSaveButton
                isDisabled={!isFormAssignEnabled}
                info={{ text: 'Assign', action: 'success' }}
                onPress={() => handleSubmit(onSave)()}
                containerStyle={style.submitButton}
              />
            ) : null}
          </Box>
        </ModalKeyboardStickyView>
      </FormContentBoundary>
    </>
  );
}

type AssignmentActor = {
  avatar_id?: string | null;
  color?: string | null;
  name?: string | null;
};

type AssignmentPerson = {
  avatar_id?: string | null;
  color?: string | null;
  name?: string | null;
};

type AssignmentAttribute =
  BadgeAssigneeEditFormValues['badge_attributes'][number];

function AssignmentOverviewCard({
  assignee,
  badgeColor,
  eventId,
  eventTitle,
  eventType,
  isBulkCreate,
  person,
  targetLabel,
  teamId,
  teamTitle,
}: {
  assignee?: {
    created_at?: string | null;
    creator?: AssignmentActor | null;
    updated_at?: string | null;
    updater?: AssignmentActor | null;
  } | null;
  badgeColor?: string | null;
  eventId?: string;
  eventTitle?: string | null;
  eventType?: string | null;
  isBulkCreate: boolean;
  person?: AssignmentPerson | null;
  targetLabel?: string | null;
  teamId?: string;
  teamTitle?: string | null;
}) {
  const resolvedTargetLabel = targetLabel || 'Selected member';
  const shouldShowUpdater = shouldShowUpdatedActivity(
    assignee?.created_at,
    assignee?.updated_at,
  );
  const contextTitle = getAssignmentContextTitle({
    eventId,
    eventTitle,
    teamId,
    teamTitle,
  });
  const contextLabel = getAssignmentContextLabel({
    eventId,
    eventType,
    teamId,
  });

  return (
    <Box direction="vertical" space="lg">
      <Box style={style.assignmentInfoGrid}>
        <Card
          p="lg"
          style={[style.inspectorCard, style.assignmentPrimaryGridItem]}
        >
          <AssignmentInfoItem
            accessory={
              <Avatar
                id={isBulkCreate ? '' : person?.avatar_id || ''}
                color={person?.color || badgeColor || ''}
                name={resolvedTargetLabel}
                size="lg"
              />
            }
            label="Assigned To"
            subtitle={isBulkCreate ? 'Recipients' : 'Person'}
            title={resolvedTargetLabel}
          />
        </Card>
        <Card
          p="lg"
          style={[style.inspectorCard, style.assignmentPrimaryGridItem]}
        >
          <Text
            fontWeight="bold"
            numberOfLines={1}
            size="xs"
            variant="typographyMuted"
          >
            CONTEXT
          </Text>
          <AssignmentInfoItem
            iconName={getAssignmentContextIconName({
              eventId,
              eventType,
              teamId,
            })}
            label={contextLabel}
            title={contextTitle}
            subtitle={getAssignmentContextSubtitle({
              eventTitle,
              teamTitle,
            })}
          />
          {assignee ? (
            <Box pt="lg" style={style.assignmentContextDivider}>
              <AssignmentInfoItem
                actor={assignee.creator}
                label="Assigned By"
                subtitle={formatAssignmentDateTime(assignee.created_at)}
                title={assignee.creator?.name || 'Pending'}
              />
            </Box>
          ) : null}
          {assignee && shouldShowUpdater ? (
            <Box pt="lg" style={style.assignmentContextDivider}>
              <AssignmentInfoItem
                actor={assignee.updater}
                iconName="time-outline"
                label="Updated By"
                subtitle={formatAssignmentDateTime(assignee.updated_at)}
                title={assignee.updater?.name || 'Unknown'}
              />
            </Box>
          ) : null}
        </Card>
      </Box>
    </Box>
  );
}

function AssignmentInfoItem({
  accessory,
  actor,
  iconName,
  label,
  subtitle,
  title,
}: {
  actor?: AssignmentActor | null;
  accessory?: ReactNode;
  iconName?:
    | 'calendar-outline'
    | 'globe-outline'
    | 'people-outline'
    | 'time-outline';
  label: string;
  subtitle?: string | null;
  title: string;
}) {
  const resolvedAccessory =
    accessory ??
    (actor ? (
      <Avatar
        id={actor.avatar_id || ''}
        color={actor.color || ''}
        name={actor.name || ''}
        size="md"
      />
    ) : null);

  return (
    <Box direction="horizontal" align="center" space="md">
      {resolvedAccessory}
      {!resolvedAccessory && iconName ? (
        <Box center style={style.assignmentInfoIcon}>
          <Icon name={iconName} size="lg" variant="typographySecondary" />
        </Box>
      ) : null}
      <Box flexShrink={1} minWidth={0}>
        <Text
          fontWeight="bold"
          numberOfLines={1}
          size="xs"
          variant="typographyMuted"
        >
          {label}
        </Text>
        <Text flexShrink={1} fontWeight="semibold" numberOfLines={1} size="md">
          {title}
        </Text>
        {subtitle ? (
          <Text numberOfLines={1} size="xs" variant="typographySecondary">
            {subtitle}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

function AssignmentAttributeCards({
  attributes,
}: {
  attributes: AssignmentAttribute[];
}) {
  return (
    <Box direction="vertical" space="lg" style={style.responseFields}>
      {attributes.map((attribute, attributeIndex) => (
        <Card key={attribute.id} p="lg" style={style.inspectorCard}>
          <BadgeAssigneeEditAttribute
            name={`badge_attributes.${attributeIndex}.badge_attribute_assignees.0.value`}
            attribute={attribute}
            inlineMetadata
          />
        </Card>
      ))}
    </Box>
  );
}

function getAssignmentContextTitle({
  eventId,
  eventTitle,
  teamId,
  teamTitle,
}: {
  eventId?: string;
  eventTitle?: string | null;
  teamId?: string;
  teamTitle?: string | null;
}) {
  if (teamTitle) {
    return teamTitle;
  }

  if (teamId) {
    return 'Team';
  }

  if (eventId && eventTitle) {
    return eventTitle;
  }

  if (eventId) {
    return 'Event';
  }

  return 'Organization';
}

function getAssignmentContextSubtitle({
  eventTitle,
  teamTitle,
}: {
  eventTitle?: string | null;
  teamTitle?: string | null;
}) {
  if (!eventTitle || !teamTitle || teamTitle === eventTitle) {
    return undefined;
  }

  return teamTitle;
}

function getAssignmentContextLabel({
  eventId,
  eventType,
  teamId,
}: {
  eventId?: string;
  eventType?: string | null;
  teamId?: string;
}) {
  if (teamId) {
    return 'Team';
  }

  if (eventId) {
    return getEventContextLabel(eventType);
  }

  return 'Organization';
}

function getAssignmentContextIconName({
  eventId,
  eventType,
  teamId,
}: {
  eventId?: string;
  eventType?: string | null;
  teamId?: string;
}): 'calendar-outline' | 'globe-outline' | 'people-outline' {
  if (teamId) {
    return 'people-outline';
  }

  if (eventId && eventType === 'organization') {
    return 'globe-outline';
  }

  if (eventId) {
    return 'calendar-outline';
  }

  return 'globe-outline';
}

function getEventContextLabel(eventType?: string | null) {
  if (eventType === 'organization') {
    return 'Organization';
  }

  if (eventType === 'group') {
    return 'Schedule';
  }

  return 'Event';
}

function formatAssignmentDateTime(value?: string | null) {
  if (!value) {
    return '';
  }

  return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED);
}

function shouldShowUpdatedActivity(
  createdAt?: string | null,
  updatedAt?: string | null,
) {
  if (!updatedAt || updatedAt === createdAt) {
    return false;
  }

  const updatedDateTime = DateTime.fromISO(updatedAt);
  const createdDateTime = DateTime.fromISO(createdAt ?? '');

  if (!updatedDateTime.isValid || !createdDateTime.isValid) {
    return true;
  }

  return updatedDateTime.toMillis() > createdDateTime.toMillis();
}

const style = StyleSheet.create((theme) => {
  return {
    submitButton: {
      flex: 1,
    },
    inspectorContent: {
      gap: theme.space('lg'),
      paddingBottom: theme.space('xl'),
      paddingHorizontal: theme.space('xl'),
      paddingTop: theme.space('lg'),
    },
    inspectorCard: {
      gap: theme.space('lg'),
      justifyContent: 'center',
    },
    assignmentInfoGrid: {
      flexDirection: 'column',
      gap: theme.space('lg'),
    },
    assignmentPrimaryGridItem: {
      width: '100%',
    },
    assignmentContextDivider: {
      borderTopColor: theme.colors.borderMuted,
      borderTopWidth: theme.size(0.5),
    },
    assignmentInfoIcon: {
      height: theme.size(8),
      width: theme.size(8),
    },
    responseFields: {
      width: '100%',
    },
  };
});
